import { Typography, useTheme, CircularProgress } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useApiError from 'packages/novous_web/src/api/useApiError';
import AlertSettingsGrid from '../common/AlertSettingsGrid';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import Loader from 'packages/novous_web/src/common/components/Loader';
import UserSettings from '../UserSettings';
import UserAlertSettings from './UserAlertSetting';
import AlertControlSection from './AlertControlSection';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import Button from 'packages/novous_web/src/common/components/Button';

type NumberConfigItem = {
  value?: number;
  thresholdValue?: number;
  min?: number;
  max?: number;
  label?: string;
  error?: string;
  readonly?: boolean;
};

type ToggleState = {
  displayName: string;
  enabled: boolean;
  thresholdValue?: number;
  dropdownValue?: string;
  dropdownOptions?: string[];
  min?: number;
  criticalEvent?: boolean;
  max?: number;
  label?: string;
  expanded: boolean;
  error: string;
  numberConfig?: NumberConfigItem[];
  readonly?: boolean;
};

export default function AdminManageAlerts() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();

  // States
  const [alertData, setAlertData] = useState<any>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('Hindi');
  const [toggleStates, setToggleStates] = useState<{ [key: string]: ToggleState }>({});
  const [inputErrors, setInputErrors] = useState<{ [key: string]: string }>({});
  const [isSave, setIsSave] = useState<boolean>(false);
  const [isDisabled, setDisabled] = useState(true);
  const [isFetchAgain, setIsFetchAgain] = useState<boolean>(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  // const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [initialAlertData, setInitialAlertData] = useState<any>(null);

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const {
    data: companyList,
    error: companyListError,
    loading: companyListLoading,
    fetchData: fetchCompanyListData,
  } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_LIST}`, 'GET');

  const {
    data: companyListByCrn,
    error: companyListByCrnError,
    loading: companyListByCrnLoading,
    fetchData: fetchCompanyListByCrnData,
  } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, 'POST');

  const {
    data: fleetManagersByCrn,
    error: fleetManagersByCrnError,
    loading: fleetManagersByCrnLoading,
    fetchData: fetchFleetManagersByCrnData,
  } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, 'POST');

  // Manage Alerts UI API
  const {
    data: manageAlertsUi,
    fetchData: fetchManageAlertsUiData,
    loading: manageAlertsUiLoading,
  } = useAxios<any>(`${API_ENDPOINTS.users.MANAGE_ALERTS_UI}`, 'POST');

  // Save Manage Alerts API
  const {
    data: manageAlertsSaveData,
    loading: manageAlertsSaveLoading,
    error: manageAlertsSaveError,
    fetchData: fetchManageAlertsSaveData,
  } = useApiError<any>(`${API_ENDPOINTS.users.MANAGE_ALERT_SAVE}`, 'POST');

  useEffect(() => {
    if (manageAlertsSaveData) {
      setToast({
        open: true,
        message: manageAlertsSaveData.message,
        type: 'success',
      });
      setSelectedCompany(null);
      setSelectedUser(null);
      setTimeout(() => {
        // setAlertData(null);
        fetchManageAlertsUiData({ fleetId: user?.customer_id });
        setIsFetchAgain(!isFetchAgain);
        setToggleStates({});
      }, 1000);
    }
  }, [manageAlertsSaveData]);

  useEffect(() => {
    if (user?.customer_id) {
      fetchManageAlertsUiData({ fleetId: user?.customer_id });
    }
    if (user?.authorities === 'ROLE_SUPER_ADMIN') {
      fetchCompanyListData();
    }
    if (user?.authorities === 'ROLE_ADMIN' && user?.crn) {
      fetchFleetManagersByCrnData({ userId: user?.id, userCrn: user?.crn });
    }
  }, []);

  useEffect(() => {
    if (Object.keys(inputErrors).length == 0 && user?.authorities !== 'ROLE_SUPER_ADMIN') {
      setDisabled(false);
    } else if (user?.authorities === 'ROLE_SUPER_ADMIN') {
      setDisabled(true);
    } else {
      setDisabled(true);
    }
  }, [inputErrors]);

  // Process fetched alerts config
  useEffect(() => {
    if (manageAlertsUi && manageAlertsUi.data && manageAlertsUi.data.manageAlerts) {
      const data = manageAlertsUi.data.manageAlerts;
      setAlertData(data);

      const configState: { [key: string]: ToggleState } = {};
      Object.entries(data).forEach(([key, settingsArray]: [string, any]) => {
        const displayName = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
        const booleanSetting = settingsArray.find((s: { type: string }) => s.type === 'boolean');
        const numberSettings = settingsArray.filter((s: { type: string }) => s.type === 'Number');
        const dropdownSetting = settingsArray.find((s: { type: string }) => s.type === 'dropdown');

        const numberConfig: NumberConfigItem[] = [];
        numberSettings.forEach((numberSetting: any, index: number) => {
          if (Array.isArray(numberSetting.value)) {
            // If the value itself is an array, create multiple config items
            numberSetting.value.forEach((value: number, idx: number) => {
              const minValue = Array.isArray(numberSetting.min) ? numberSetting.min[idx] : numberSetting.min;
              const maxValue = Array.isArray(numberSetting.max) ? numberSetting.max[idx] : numberSetting.max;
              const labelValue = Array.isArray(numberSetting.label)
                ? numberSetting.label[idx]
                : numberSetting.label || `Threshold ${idx + 1}`;

              const readonly = Array.isArray(numberSetting.readonly)
                ? numberSetting.readonly[idx]
                : numberSetting.readonly || false;

              numberConfig.push({
                value: value,
                thresholdValue: value,
                min: minValue,
                max: maxValue,
                label: labelValue,
                error: '',
                readonly: readonly,
              });
            });
          } else {
            // Single value
            numberConfig.push({
              value: numberSetting.value,
              thresholdValue: numberSetting.value,
              min: numberSetting.min,
              max: numberSetting.max,
              label: numberSetting.label,
              error: '',
              readonly: numberSetting.readonly || false,
            });
          }
        });

        // Determine if we should expand based on number configs
        const hasNumberConfigs = numberConfig.length > 0;
        const hasSingleNumberValue = numberConfig.length === 1 && numberConfig[0].value !== undefined;
        configState[key] = {
          displayName,
          enabled: booleanSetting?.value ?? booleanSetting?.enable ?? false,
          thresholdValue: hasSingleNumberValue ? numberConfig[0].value : undefined,
          dropdownValue: dropdownSetting?.value,
          dropdownOptions: dropdownSetting?.options,
          criticalEvent: booleanSetting?.criticalEvent ?? false,
          min: hasSingleNumberValue ? numberConfig[0].min : undefined,
          max: hasSingleNumberValue ? numberConfig[0].max : undefined,
          label: hasSingleNumberValue ? numberConfig[0].label : undefined,
          numberConfig: numberConfig.length > 0 ? numberConfig : undefined, // Add the array
          expanded: hasNumberConfigs || typeof dropdownSetting?.value === 'string',
          readonly: hasSingleNumberValue ? numberConfig[0]?.readonly : false,
          error: '',
        };
      });

      setToggleStates(configState);
    }
  }, [manageAlertsUi]);

  // Language dropdown options
  const languageData = alertData?.warningAudioLanguage?.[0];

  const languageOptions = (languageData?.options || []).map((lang: string) => ({
    label: lang,
    value: lang,
  }));

  // Expand/Collapse handler
  const toggleExpand = (key: string) => {
    setToggleStates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        expanded: !prev[key].expanded,
      },
    }));
  };

  useEffect(() => {
    if (manageAlertsUi?.data?.manageAlerts) {
      const data = manageAlertsUi.data.manageAlerts;
      setAlertData(data);
      setInitialAlertData(JSON.parse(JSON.stringify(data)));
    }
  }, [manageAlertsUi]);

  function getChangedAlerts(initial: Record<string, any>, current: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    for (const key in current) {
      if (!initial[key]) {
        result[key] = current[key];
        continue;
      }

      const initialSection = initial[key];
      const currentSection = current[key];

      // Deep compare function for alert items
      const compareAlertItems = (initialItem: any, currentItem: any): boolean => {
        // If types don't match, they're different
        if (initialItem.type !== currentItem.type) return false;

        // For boolean items, compare value and criticalEvent
        if (initialItem.type === 'boolean') {
          return (
            initialItem.value === currentItem.value && initialItem.criticalEvent === currentItem.criticalEvent
          );
        }

        // For dropdown items, compare value
        if (initialItem.type === 'dropdown') {
          return initialItem.value === currentItem.value;
        }

        // For Number items, we need special handling for arrays
        if (initialItem.type === 'Number') {
          // Handle both array and single value cases
          const initialValue = initialItem.value;
          const currentValue = currentItem.value;

          if (Array.isArray(initialValue) && Array.isArray(currentValue)) {
            // Compare arrays
            if (initialValue.length !== currentValue.length) return false;
            return initialValue.every((val: number, idx: number) => val === currentValue[idx]);
          }

          // Compare single values
          return initialValue === currentValue;
        }

        // For other types, do deep comparison
        return JSON.stringify(initialItem) === JSON.stringify(currentItem);
      };

      // Check if any item in the section has changed
      const hasChanged =
        initialSection.some((initialItem: any, index: number) => {
          const currentItem = currentSection[index];

          // If current doesn't have this item, it's a change
          if (!currentItem) return true;

          // Compare the items
          return !compareAlertItems(initialItem, currentItem);
        }) ||
        currentSection.some((currentItem: any, index: number) => {
          // Also check if current has extra items that initial doesn't have
          return !initialSection[index];
        });

      if (hasChanged) {
        result[key] = currentSection;
        // console.log(`Section ${key} changed:`, {
        //   initial: initialSection,
        //   current: currentSection
        // });
      }
    }

    //console.log('Changed alerts result:', result);
    return result;
  }

  // Save payload handler
  const handleSave = () => {
    if (Object.keys(inputErrors).length > 0) {
      setToast({
        open: true,
        message: 'Please fix validation errors before saving.',
        type: 'error',
      });
      return;
    }

    if (user?.authorities === 'ROLE_SUPER_ADMIN') {
      setIsSave(true);
      return;
    }

    // For non-admin users, proceed with alert data saving
    if (!alertData || Object.keys(alertData).length === 0) {
      setToast({
        open: true,
        message: 'Alert data not loaded yet',
        type: 'error',
      });
      return;
    }

    const modifiedAlertData = JSON.parse(JSON.stringify(alertData));

    // Transform the enable properties to value
    Object.keys(modifiedAlertData).forEach((key) => {
      const settingsArray = modifiedAlertData[key];
      settingsArray.forEach((setting: any) => {
        if (setting.type === 'boolean') {
          // Ensure we're saving the correct value
          setting.value = toggleStates[key]?.enabled ?? setting.value;
          // Remove the enable property if it exists
          if ('enable' in setting) {
            delete setting.enable;
          }
        }
        // Handle dropdown values
        if (setting.type === 'dropdown') {
          setting.value = toggleStates[key]?.dropdownValue ?? setting.value;
        }
      });
    });

    const changedAlerts = getChangedAlerts(initialAlertData, alertData);
    // console.log('handle save clicked', changedAlerts);

    if (Object.keys(changedAlerts).length === 0) {
      setIsSave(true);
      return;
    }

    const payload = {
      username: user?.fullName,
      password: user?.password,
      manageAlerts: changedAlerts,
      fleetId: user?.customer_id || selectedUser?.customer_id,
    };
    setIsSave(true);
    fetchManageAlertsSaveData(payload);
  };

  // Toggle on/off handler
  const handleToggle = (key: string) => {
    const isCurrentlyEnabled = toggleStates[key].enabled;
    setToggleStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], enabled: !isCurrentlyEnabled },
    }));

    const updatedArray = [...(alertData[key] || [])];
    const booleanSettingIndex = updatedArray.findIndex((item) => item.type === 'boolean');
    if (booleanSettingIndex !== -1) {
      updatedArray[booleanSettingIndex].value = !isCurrentlyEnabled;
      updatedArray[booleanSettingIndex].enable = !isCurrentlyEnabled;
      setAlertData({
        ...alertData,
        [key]: updatedArray,
      });
    }
  };

  const handleVideoToggle = (key: string, videoName: string, newValue: boolean) => {
    // Update alertData
    const updatedArray = [...(alertData[key] || [])];
    const videoConfigIndex = updatedArray.findIndex((item: any) => item.name === videoName);

    if (videoConfigIndex !== -1) {
      updatedArray[videoConfigIndex].value = newValue;
      setAlertData({
        ...alertData,
        [key]: updatedArray,
      });
    }
  };

  const handleInputChange = (key: string, idx: number, newValue: number) => {
    //console.log('handleInputChange called:', { key, idx, newValue });

    const toggleState = toggleStates[key];
    if (!toggleState) {
      console.error('Toggle state not found for key:', key);
      return;
    }

    const { numberConfig } = toggleState;

    // Handle case when numberConfig doesn't exist (backward compatibility)
    if (!numberConfig || numberConfig.length === 0) {
      console.log('No numberConfig found, using single value handling');

      const { min, max } = toggleState;
      let error = '';

      // Validate value
      if (newValue < (min ?? -Infinity) || newValue > (max ?? Infinity)) {
        error = `Value must be between ${min} and ${max}`;
      }

      // Update error state
      if (error !== '') {
        setInputErrors((prev) => ({
          ...prev,
          [key]: error,
        }));
      } else {
        setInputErrors((prev) => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });
      }

      // Update toggleStates for single value
      setToggleStates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          error,
          thresholdValue: newValue,
        },
      }));

      // Update alertData for single value
      const updatedArray = [...(alertData[key] || [])];
      const numberSettingIndex = updatedArray.findIndex((item) => item.type === 'Number');
      if (numberSettingIndex !== -1) {
        updatedArray[numberSettingIndex].value = newValue;
        setAlertData({
          ...alertData,
          [key]: updatedArray,
        });
      }

      return;
    }

    // Handle array case (numberConfig exists)
    //console.log('numberConfig found:', numberConfig);

    // Validate idx is within bounds
    if (idx < 0 || idx >= numberConfig.length) {
      console.error('Invalid index:', idx, 'for numberConfig length:', numberConfig.length);
      return;
    }

    const configItem = numberConfig[idx];
    //console.log('Config item at index', idx, ':', configItem);

    const { min, max } = configItem;
    let error = '';

    // Validate value
    if (min !== undefined && newValue < min) {
      error = `Value must be at least ${min}`;
    } else if (max !== undefined && newValue > max) {
      error = `Value must be at most ${max}`;
    }

    //console.log('Validation result:', { min, max, newValue, error });

    // Update toggleStates - update the specific item in numberConfig array
    setToggleStates((prev) => {
      const updatedState = {
        ...prev,
        [key]: {
          ...prev[key],
          // Keep error at parent level for backward compatibility
          error: prev[key].numberConfig?.some((item) => item.error) ? 'Some values have errors' : '',
          // Update specific item in numberConfig array
          numberConfig: prev[key].numberConfig?.map((item, index) =>
            index === idx ? { ...item, value: newValue, thresholdValue: newValue, error } : item,
          ),
          // Also update thresholdValue if this is the first item (for backward compatibility)
          thresholdValue: idx === 0 ? newValue : prev[key].thresholdValue,
        },
      };

      //console.log('Updated toggleStates:', updatedState[key]);
      return updatedState;
    });

    // Update alertData
    // Update alertData
    setAlertData((prevAlertData: any) => {
      if (!prevAlertData || !prevAlertData[key]) return prevAlertData;

      const updatedArray = [...prevAlertData[key]];

      // Find all Number type items
      const numberItems = updatedArray.filter((item: any) => item.type === 'Number');

      //console.log('Number items found:', numberItems.length);

      if (numberItems.length === 0) {
        console.log('No Number type found in alertData for key:', key);
        return prevAlertData;
      }

      // We need to map the flat idx to the correct item and position
      let currentIdx = 0;
      let updated = false;

      for (const item of numberItems) {
        const itemIndex = updatedArray.findIndex((i) => i === item);

        if (Array.isArray(item.value)) {
          // This item has an array value
          const arrayLength = item.value.length;

          // Check if our target idx is within this array
          if (idx >= currentIdx && idx < currentIdx + arrayLength) {
            const arrayIndex = idx - currentIdx;
            const newValueArray = [...item.value];
            newValueArray[arrayIndex] = newValue;
            updatedArray[itemIndex].value = newValueArray;
            updated = true;
            //console.log(`Updated array item ${item.name} at array index ${arrayIndex}:`, newValueArray);
            break;
          }

          currentIdx += arrayLength;
        } else {
          // This item has a single value
          if (idx === currentIdx) {
            updatedArray[itemIndex].value = newValue;
            updated = true;
            // console.log(`Updated single value item ${item.name}:`, newValue);
            break;
          }

          currentIdx += 1;
        }
      }

      if (!updated) {
        console.error(`Could not find position for idx ${idx} in number items`);
      }

      return {
        ...prevAlertData,
        [key]: updatedArray,
      };
    });
  };

  // Dropdown change handler
  const handleDropdownChange = (key: string, newValue: string) => {
    // Update toggleStates
    setToggleStates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        dropdownValue: newValue,
      },
    }));

    // Update alertData
    const updatedArray = [...(alertData[key] || [])];
    const dropdownSettingIndex = updatedArray.findIndex((item) => item.type === 'dropdown');
    if (dropdownSettingIndex !== -1) {
      updatedArray[dropdownSettingIndex].value = newValue;
      setAlertData({
        ...alertData,
        [key]: updatedArray,
      });
    }
  };

  const handleCritialEvent = (key: string, criticalEvent: boolean) => {
    setToggleStates((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        criticalEvent,
      },
    }));
    const updatedArray = [...(alertData[key] || [])].map((setting) => ({
      ...setting,
      criticalEvent,
    }));
    const booleanSettingIndex = updatedArray.findIndex((item) => item.type === 'boolean');
    if (booleanSettingIndex !== -1) {
      updatedArray[booleanSettingIndex].criticalEvent = criticalEvent;
      setAlertData({
        ...alertData,
        [key]: updatedArray,
      });
    }
  };

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage);

    // Update the alertData with the new language selection
    if (alertData?.warningAudioLanguage) {
      const updatedAlertData = {
        ...alertData,
        warningAudioLanguage: [
          {
            ...alertData.warningAudioLanguage[0],
            value: newLanguage,
          },
        ],
      };
      setAlertData(updatedAlertData);
    }
  };

  const handleCompanyChange = (value: string) => {
    const company = companyList?.data?.find((c: any) => c.name === value);
    setSelectedCompany(company || null);
    setSelectedUser(null);

    if (company) {
      fetchCompanyListByCrnData({
        userId: company.id,
        userCrn: company.crn,
      });
    }
  };

  const handleUserChange = (value: string) => {
    const userSelected = companyListByCrn?.data?.find((u: any) => u.fullName === value);
    setSelectedUser(userSelected || null);

    if (userSelected) {
      fetchManageAlertsUiData({
        fleetId:
          userSelected?.authorities === 'ROLE_SUPER_ADMIN' ? userSelected?.id : userSelected?.customer_id,
      });
    }
  };

  const handleFleetManagerChange = (value: string) => {
    const fmSelected = fleetManagersByCrn?.data?.find((u: any) => u.fullName === value);
    setSelectedUser(fmSelected || null);
    if (fmSelected) {
      fetchManageAlertsUiData({
        fleetId: fmSelected?.id,
      });
    }
  };

  return (
    <>
      {(manageAlertsSaveLoading || manageAlertsUiLoading) && <Loader />}

      <div className="flex flex-col gap-4 w-full">
        <div className="w-full">
          <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
            <ArrowBack className="mr-1" /> Back
          </button>
        </div>
        <div className="w-full flex flex-col justify-between gap-4 items-start">
          <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight="36px">
            Manage Alerts
          </Typography>

          {user?.authorities === 'ROLE_SUPER_ADMIN' && (
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <CustomDropDownInput
                label="Company"
                options={
                  companyList?.data?.map((company: any) => ({
                    value: company.name,
                    label: company.name,
                  })) || []
                }
                value={selectedCompany?.name || ''}
                onChange={handleCompanyChange}
                placeholder="Select Company"
                showDropdownArrow={true}
              />
              <CustomDropDownInput
                label={selectedCompany ? 'User' : 'Select company first'}
                options={
                  companyListByCrn?.data?.map((user: any) => ({
                    value: user.fullName,
                    label: user.fullName,
                  })) || []
                }
                value={selectedUser?.fullName || ''}
                onChange={handleUserChange}
                placeholder={selectedCompany ? 'Select User' : 'Select company first'}
                isDisabled={!selectedCompany}
                showDropdownArrow={true}
              />
            </div>
          )}
        </div>

        {user?.authorities === 'ROLE_ADMIN' && (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <CustomDropDownInput
              label="Fleet Manager"
              options={
                fleetManagersByCrn?.data?.map((fm: any) => ({
                  value: fm.fullName,
                  label: fm.fullName,
                })) || []
              }
              value={selectedUser?.fullName || ''}
              onChange={handleFleetManagerChange}
              placeholder={'Select Fleet Manager'}
              showDropdownArrow={true}
            />
          </div>
        )}

        {(user?.authorities === 'ROLE_SUPER_ADMIN' || user?.authorities === 'ROLE_ADMIN') && (
          <AlertControlSection selectedCompany={selectedCompany} selectedUser={selectedUser} />
        )}
        <div
          className="w-full flex flex-col gap-6"
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            padding: '24px',
            backgroundColor: '#fafafa',
            marginBottom: '24px',
          }}
        >
          {user?.authorities !== 'ROLE_SUPER_ADMIN' && (
            <>
              <div className="w-[50%] flex justify-around gap-4">
                <CustomDropDownInput
                  label="Alert Language"
                  options={languageOptions}
                  value={alertData?.warningAudioLanguage?.[0]?.value}
                  onChange={handleLanguageChange} // Use the new handler
                  placeholder={languageOptions[0]?.label || 'Select Language'}
                  showDropdownArrow
                />
              </div>
              <div className="w-full flex flex-col gap-4">
                <Typography variant="h6" fontSize="18px" fontWeight={600} lineHeight="36px">
                  Alert Configuration
                </Typography>

                <AlertSettingsGrid
                  alertData={alertData}
                  alertConfigState={toggleStates}
                  theme={theme}
                  onToggle={handleToggle}
                  onExpand={toggleExpand}
                  onInputChange={handleInputChange}
                  onCheckCritical={handleCritialEvent}
                  onDropdownChange={handleDropdownChange}
                  onVideoToggle={handleVideoToggle}
                />
              </div>
            </>
          )}
          {user.authorities !== 'ROLE_ADMIN' && (
            <UserAlertSettings
              isSave={isSave}
              setSave={setIsSave}
              handleDisabled={setDisabled}
              isFetchAgain={isFetchAgain}
              selectedCompany={selectedCompany}
              selectedUser={selectedUser}
            />
          )}
          <div className="flex gap-2 justify-end mt-6">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline={true}
              padding="0px"
              variant="outlined"
              onClick={() => {
                navigate(-1);
              }}
            />
            <Button text="Save" disabled={isDisabled} onClick={handleSave} fullWidth={false} />
          </div>
        </div>
      </div>
      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
}
